import { Children, cloneElement, isValidElement, useId, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { useHintTip } from './HintTip';

const CONTROLS = new Set(['input', 'select', 'textarea']);

interface Props {
  label: string;
  /** Erklärt kurz, was in das Feld gehört: als Tooltip auf Beschriftung und Eingabefeld */
  subtitle?: string;
  help?: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  htmlFor?: string;
}

/**
 * Beschriftetes Formularfeld. Hat das Feld genau ein Eingabeelement, wird es per `for`/`id` mit dem Label verknüpft;
 * bei zusammengesetzten Inhalten (z. B. Chips) wird die Gruppe über `aria-labelledby` benannt.
 */
export function Field({ label, subtitle, help, children, className, style, htmlFor }: Props) {
  const uid = useId();
  const hint = useHintTip(subtitle);
  const only = Children.count(children) === 1 && isValidElement(children) && typeof children.type === 'string' && CONTROLS.has(children.type);
  const control = only ? (children as ReactElement<{ id?: string; 'aria-describedby'?: string }>) : null;
  const id = control?.props.id ?? htmlFor ?? `${uid}-c`;
  const helpId = help ? `${uid}-h` : undefined;
  const describedBy = [hint.describedBy, helpId].filter(Boolean).join(' ') || undefined;
  return (
    <div
      className={`field${className ? ` ${className}` : ''}`}
      style={style}
      {...hint.bind}
      {...(!only && !htmlFor ? { role: 'group', 'aria-labelledby': `${uid}-l`, 'aria-describedby': hint.describedBy } : {})}
    >
      <label id={`${uid}-l`} htmlFor={only || htmlFor ? id : undefined}>
        {label}
      </label>
      {control ? cloneElement(control, { id, ...(describedBy ? { 'aria-describedby': describedBy } : {}) }) : children}
      {help && (
        <div className="help" id={helpId}>
          {help}
        </div>
      )}
      {hint.tip}
    </div>
  );
}
